import { ShieldCheck, Compass, Building2, Rocket, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, Overline, PrimaryLink } from "@/components/site";
import { PILLARS } from "@/data/content";

const ICONS = { technology: Compass, cyber: ShieldCheck, governance: Building2, transformation: Rocket };
const IMAGES = {
  technology: "https://images.unsplash.com/photo-1729371568794-fb9c66ab09cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  cyber: "https://images.unsplash.com/photo-1758518730037-a16581a040e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  governance: "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  transformation: "https://images.unsplash.com/photo-1573879403319-4412126a561b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function Services() {
  return (
    <div data-testid="page-services">
      <SEO
        title="Advisory Pillars — Technology & Cyber Security"
        description="TWORAY advisory pillars: Technology Advisory, Cyber Security Advisory, Governance & Resilience, and Transformation & Enablement. Independent advice, advice-first."
      />
      <PageHero
        overline="Advisory Pillars"
        title="Four disciplines, one independent advisory partner."
        subtitle="Independent advice across the areas that matter to growing organisations — advice first, implementation where it adds value and is explicitly scoped."
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 space-y-20 sm:space-y-28">
          {PILLARS.map((p, i) => {
            const Icon = ICONS[p.key];
            const reversed = i % 2 === 1;
            return (
              <Reveal key={p.key}>
                <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}>
                  <div className="[direction:ltr]">
                    <div className="relative overflow-hidden rounded-md border border-brandborder aspect-[4/3]">
                      <img src={IMAGES[p.key]} alt={p.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-navy/10" />
                      <div className="absolute top-5 left-5 bg-white/90 backdrop-blur rounded-sm px-3 py-1.5 flex items-center gap-2">
                        <Icon size={16} className="text-brandblue" />
                        <span className="text-xs font-semibold text-navy">{p.title}</span>
                      </div>
                    </div>
                  </div>
                  <div className="[direction:ltr]" data-testid={`service-block-${p.key}`}>
                    <span className="font-heading font-extrabold text-6xl text-brandborder">{p.num}</span>
                    <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-navy">{p.title}</h2>
                    <p className="mt-4 text-slate text-lg leading-relaxed">{p.summary}</p>
                    <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                      {p.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2 text-navy">
                          <span className="mt-2 w-1.5 h-1.5 bg-cyan rounded-full shrink-0" />
                          <span className="text-sm font-medium">{pt}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/individual-services" className="mt-7 inline-flex items-center gap-1.5 text-brandblue font-semibold text-sm link-underline pb-0.5">
                      See related capabilities <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="bg-navy rounded-md p-10 sm:p-14 text-white flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <Overline dark>Not sure where to start?</Overline>
                <h3 className="mt-3 font-heading font-bold text-2xl sm:text-3xl">Book a discovery call and we'll point you the right way.</h3>
              </div>
              <PrimaryLink to="/book" dark data-testid="services-book-cta">Book a Discovery Call</PrimaryLink>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
